import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, ChevronDown, Download, Building2, Users, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { monthKey, monthLabel, fmtHours, formatDateIT } from "@/lib/dates";

export default function AdminReports() {
  const [cursor, setCursor] = useState(new Date());
  const [view, setView] = useState("company"); // company | employee
  const [companyData, setCompanyData] = useState(null);
  const [employeeData, setEmployeeData] = useState(null);
  const [selected, setSelected] = useState(null); // null = all
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState("");
  const [expandedEmp, setExpandedEmp] = useState(null);
  const [empDetail, setEmpDetail] = useState({});

  const month = monthKey(cursor);

  const load = async () => {
    setLoading(true);
    try {
      const [c, e] = await Promise.all([
        api.get(`/reports/companies?month=${month}`),
        api.get(`/reports/employees?month=${month}`),
      ]);
      setCompanyData(c.data);
      setEmployeeData(e.data);
      setSelected(null);
      setExpandedEmp(null);
      setEmpDetail({});
    } catch {
      toast.error("Errore nel caricamento del report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  const move = (delta) => {
    const d = new Date(cursor);
    d.setMonth(d.getMonth() + delta);
    setCursor(d);
  };

  const download = async (type) => {
    setDownloading(type);
    try {
      const res = await api.get(`/reports/export/${type}?month=${month}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `report_${type === "companies" ? "aziende" : "dipendenti"}_${month}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel scaricato");
    } catch {
      toast.error("Errore durante l'esportazione");
    } finally {
      setDownloading("");
    }
  };

  const toggleEmp = async (emp) => {
    if (expandedEmp === emp.employee_id) {
      setExpandedEmp(null);
      return;
    }
    setExpandedEmp(emp.employee_id);
    if (!empDetail[emp.employee_id]) {
      try {
        const res = await api.get(`/hours/admin?month=${month}&employee_id=${emp.employee_id}`);
        const sorted = [...res.data].sort((a, b) =>
          (a.work_date + a.start_time).localeCompare(b.work_date + b.start_time)
        );
        setEmpDetail((prev) => ({ ...prev, [emp.employee_id]: sorted }));
      } catch {
        toast.error("Errore nel caricamento del dettaglio");
      }
    }
  };

  const companies = companyData?.companies || [];
  const visibleCompanies = selected ? companies.filter((c) => selected.includes(c.job_id)) : companies;

  return (
    <div className="space-y-4">
      <h1 className="font-head text-2xl font-bold text-slate-900">Report ore</h1>

      {/* Month nav */}
      <div className="flex items-center justify-between bg-white border border-slate-200 rounded-xl p-2">
        <button data-testid="report-prev-month" onClick={() => move(-1)} className="h-11 w-11 rounded-lg border border-slate-200 flex items-center justify-center active:scale-95">
          <ChevronLeft className="h-6 w-6" />
        </button>
        <span data-testid="report-month" className="font-head font-bold text-lg">{monthLabel(cursor)}</span>
        <button data-testid="report-next-month" onClick={() => move(1)} className="h-11 w-11 rounded-lg border border-slate-200 flex items-center justify-center active:scale-95">
          <ChevronRight className="h-6 w-6" />
        </button>
      </div>

      {/* View toggle */}
      <div className="grid grid-cols-2 gap-2 bg-slate-100 p-1 rounded-xl">
        <button data-testid="view-company" onClick={() => setView("company")} className={`h-11 rounded-lg font-bold flex items-center justify-center gap-1.5 text-sm ${view === "company" ? "bg-white text-primary shadow-sm" : "text-slate-500"}`}>
          <Building2 className="h-4 w-4" /> Per azienda
        </button>
        <button data-testid="view-employee" onClick={() => setView("employee")} className={`h-11 rounded-lg font-bold flex items-center justify-center gap-1.5 text-sm ${view === "employee" ? "bg-white text-primary shadow-sm" : "text-slate-500"}`}>
          <Users className="h-4 w-4" /> Per dipendente
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
      ) : view === "company" ? (
        <>
          <button data-testid="export-companies" onClick={() => download("companies")} disabled={downloading} className="w-full h-12 rounded-xl border border-primary text-primary font-bold flex items-center justify-center gap-2 active:scale-[0.98]">
            {downloading === "companies" ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Download className="h-5 w-5" /> Esporta Excel aziende</>}
          </button>

          {/* Company filter chips */}
          {companies.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setSelected(null)} className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${!selected ? "bg-primary text-white border-primary" : "border-slate-200 text-slate-600"}`}>
                Tutte
              </button>
              {companies.map((c) => {
                const on = selected?.includes(c.job_id);
                return (
                  <button
                    key={c.job_id}
                    data-testid={`filter-${c.job_id}`}
                    onClick={() => setSelected((prev) => {
                      const base = prev || [];
                      return base.includes(c.job_id) ? base.filter((x) => x !== c.job_id) : [...base, c.job_id];
                    })}
                    className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${on ? "bg-primary text-white border-primary" : "border-slate-200 text-slate-600"}`}
                  >
                    {c.name}
                  </button>
                );
              })}
            </div>
          )}

          {/* Grid */}
          {visibleCompanies.length === 0 ? (
            <p className="text-slate-400 text-sm">Nessun dato per questo mese.</p>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto" data-testid="company-report-grid">
              <table className="text-sm border-collapse">
                <thead>
                  <tr>
                    <th className="sticky left-0 bg-slate-50 z-10 text-left px-3 py-2 font-bold border-b border-r border-slate-200 min-w-[130px]">Azienda</th>
                    {companyData.days.map((d) => (
                      <th key={d} className="px-2 py-2 text-center font-semibold border-b border-slate-200 text-slate-500 min-w-[36px]">{d}</th>
                    ))}
                    <th className="px-3 py-2 text-center font-bold border-b border-l border-slate-200 bg-slate-50">Tot</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleCompanies.map((c) => (
                    <tr key={c.job_id}>
                      <td className="sticky left-0 bg-white z-10 px-3 py-2 font-semibold border-b border-r border-slate-200 whitespace-nowrap">
                        {c.name}{!c.active && <span className="text-xs text-slate-400"> (elim.)</span>}
                      </td>
                      {companyData.days.map((d) => {
                        const v = c.daily[d] || c.daily[String(d)] || 0;
                        return (
                          <td key={d} className={`px-2 py-2 text-center border-b border-slate-100 ${v ? "font-bold text-primary" : "text-slate-300"}`}>
                            {v ? fmtHours(v) : "·"}
                          </td>
                        );
                      })}
                      <td className="px-3 py-2 text-center font-bold border-b border-l border-slate-200 bg-slate-50">{fmtHours(c.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <>
          <button data-testid="export-employees" onClick={() => download("employees")} disabled={downloading} className="w-full h-12 rounded-xl border border-primary text-primary font-bold flex items-center justify-center gap-2 active:scale-[0.98]">
            {downloading === "employees" ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Download className="h-5 w-5" /> Esporta Excel dipendenti</>}
          </button>
          <p className="text-xs text-slate-400 -mt-1">Tocca un dipendente per vedere il dettaglio giorno per giorno</p>
          <div className="space-y-2">
            {(employeeData?.employees || []).map((emp) => {
              const open = expandedEmp === emp.employee_id;
              const detail = empDetail[emp.employee_id] || [];
              return (
                <div key={emp.employee_id} data-testid="employee-report-card" className="bg-white border border-slate-200 rounded-xl p-4">
                  <button data-testid={`emp-toggle-${emp.employee_id}`} onClick={() => toggleEmp(emp)} className="w-full flex items-center justify-between text-left">
                    <span className="flex items-center gap-1.5">
                      <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform ${open ? "rotate-0" : "-rotate-90"}`} />
                      <span className="font-semibold text-slate-900 text-lg">{emp.name}</span>
                    </span>
                    <span className="font-head font-extrabold text-primary text-xl">{fmtHours(emp.total)} h</span>
                  </button>
                  {emp.by_company.length > 0 && (
                    <div className="mt-2 space-y-1 pl-6">
                      {emp.by_company.map((bc) => (
                        <div key={bc.name} className="flex justify-between text-sm text-slate-600">
                          <span>{bc.name}</span>
                          <span className="font-semibold">{fmtHours(bc.hours)} h</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {open && (
                    <div data-testid={`emp-daily-${emp.employee_id}`} className="mt-3 border-t border-slate-200 pt-3 space-y-1.5">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Dettaglio giornaliero</p>
                      {detail.length === 0 ? (
                        <p className="text-sm text-slate-400">Nessuna ora registrata questo mese.</p>
                      ) : (
                        detail.map((e) => (
                          <div key={e.id} className="text-sm">
                            <div className="flex items-center justify-between">
                              <span className="text-slate-700">
                                <span className="font-semibold">{formatDateIT(e.work_date)}</span> · {e.job_name}
                                <span className="text-slate-400"> ({e.start_time}–{e.end_time})</span>
                              </span>
                              <span className="font-semibold text-primary whitespace-nowrap ml-2">{fmtHours(e.hours)} h</span>
                            </div>
                            {e.note && <p className="text-slate-500 italic mt-0.5">{e.note}</p>}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
